<script setup lang="ts">
import type { SlideshowConfig } from "@/models"
import { useDeleteSlideshowConfig } from "@/api"
import { BaseModal, Button, Card } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { SlideshowDeleteModalKey } from "@/symbols"

interface SlideshowDeleteModalProps {
  slideshowConfig: SlideshowConfig
  eventVaultId?: number
}

const { slideshowConfig, eventVaultId } = defineProps<SlideshowDeleteModalProps>()

const emit = defineEmits<{
  close: []
  success: []
}>()

const { t } = useTranslation()
const { toggle } = useModal(SlideshowDeleteModalKey)
const toast = useToast()
const { mutate } = useDeleteSlideshowConfig()

function onConfirm() {
  if (!slideshowConfig.id || !eventVaultId) {
    toast.error()

    return
  }

  mutate(
    { eventVaultId, id: slideshowConfig.id },
    {
      onSuccess: () => {
        emit("success")
        onClose()
      },
      onError: () => toast.error(),
    },
  )
}

function onClose() {
  emit("close")
  toggle()
}
</script>

<template>
  <BaseModal :name="SlideshowDeleteModalKey">
    <Card>
      <template #header>
        <h1 class="font-comfortaa font-bold text-xl mb-5" data-test="delete-modal-heading">
          {{ t("slideshow_delete_modal.title") }}
        </h1>
      </template>
      <p class="font-montserrat font-medium mb-8">
        {{ t("slideshow_delete_modal.description", { name: slideshowConfig.name }) }}
      </p>
      <div class="flex gap-4 items-center justify-end">
        <Button variant="tertiary" data-test="delete-cancel-btn" @click="onClose">
          {{ t("cancel") }}
        </Button>
        <Button variant="danger" data-test="delete-confirm-btn" @click="onConfirm">
          {{ t("confirm") }}
        </Button>
      </div>
    </Card>
  </BaseModal>
</template>
